/**
 * Shared Apollo error helpers split out of `graphql-client.service.ts` so the
 * pure logic can be imported without pulling in Angular / analytics, and so the
 * auth retry link and the data-layer classifier agree on one error shape.
 */

/**
 * The terminal error emitted when a silent token refresh fails (the Apollo auth
 * retry in `graphql.providers.ts`).
 *
 * It is coded `UNAUTHORIZED` on purpose: `classifyApolloError` then treats it as
 * an EXPECTED session expiry — which still drives the logout → login redirect —
 * but `UNAUTHORIZED` is EXCLUDED from `CAPTURE_ERROR_CODES`, so it does NOT
 * reach PostHog Error Tracking.
 *
 * Previously this was a bare `new Error('Refresh failed')`, which classified as
 * `UNKNOWN` and was captured once PER concurrent boot query — laundering a
 * benign expired session into a high-volume actionable exception (the
 * "Refresh failed" spike). The object carries BOTH `graphQLErrors` and
 * `extensions` so the classifier recognises the code whichever shape it probes
 * first.
 */
export function sessionExpiredError(): Error {
  return Object.assign(new Error('Session expired'), {
    extensions: { code: 'UNAUTHORIZED' },
    graphQLErrors: [
      { message: 'Session expired', extensions: { code: 'UNAUTHORIZED' } },
    ],
  });
}

/**
 * The terminal error emitted when a token refresh reached no verdict — it was
 * rate-limited, the gateway refused it, or the request never arrived.
 *
 * It carries a `networkError` so `classifyApolloError` maps it to `NETWORK`:
 * retryable, a "couldn't reach the server" message, and excluded from
 * `CAPTURE_ERROR_CODES`. That is the honest reading — the session was NOT
 * rejected — and it keeps the surface offering a retry instead of announcing an
 * expiry and bouncing the user to login.
 */
export function refreshUnavailableError(): Error {
  return Object.assign(new Error('Could not renew the session'), {
    networkError: new Error('Token refresh unavailable'),
  });
}
